// src/components/GoogleMapsWrapper.tsx
import React from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  Box,
  Container,
  Grid,
  Paper,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HeroSectionSkeleton from '../../../../components/skeletons/HeroSectionSkeleton';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const FooterColumnSkeleton = () => {
  return (
    <Stack spacing={1.4}>
      <Skeleton variant='text' width={90} height={28} />
      <Skeleton variant='text' width={110} />
      <Skeleton variant='text' width={130} />
      <Skeleton variant='text' width={95} />
    </Stack>
  );
};

const DefaultMapFallback = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth='lg'>
        {/* TOP BAR */}
        <Paper
          elevation={0}
          sx={{
            maxWidth: '908px',
            mx: 'auto',
            mb: { xs: 4, md: 6 },
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderRadius: '999px',
            bgcolor: '#28475a',
            boxSizing: 'border-box',
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <Skeleton
              variant='rounded'
              width={isMobile ? 90 : 140}
              height={24}
              sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}
            />
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Skeleton
                variant='rounded'
                width={isMobile ? 44 : 56}
                height={28}
                sx={{
                  borderRadius: '999px',
                  bgcolor: 'rgba(255,255,255,0.18)',
                }}
              />
              <Skeleton
                variant='rounded'
                width={70}
                height={34}
                sx={{
                  borderRadius: '999px',
                  bgcolor: 'rgba(255,255,255,0.18)',
                }}
              />
              <Skeleton
                variant='circular'
                width={36}
                height={36}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}
              />
            </Stack>
          </Stack>
        </Paper>

        <HeroSectionSkeleton isDark />

        {/* FOOTER */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: { xs: '28px 28px 0 0', md: '28px' },
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          }}
        >
          <Grid container spacing={{ xs: 3, md: 6 }}>
            <Grid item xs={12} md={4}>
              <FooterColumnSkeleton />
            </Grid>
            <Grid item xs={12} md={4}>
              <FooterColumnSkeleton />
            </Grid>
            <Grid item xs={12} md={4}>
              <FooterColumnSkeleton />
            </Grid>
          </Grid>
        </Paper>

        {/* BOTTOM BRAND / SOCIAL */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent='space-between'
          alignItems='center'
          spacing={3}
          sx={{ py: { xs: 3, md: 4 } }}
        >
          <Skeleton variant='rounded' width={180} height={28} />
          <Stack direction='row' spacing={2}>
            <Skeleton variant='circular' width={40} height={40} />
            <Skeleton variant='circular' width={40} height={40} />
            <Skeleton variant='circular' width={40} height={40} />
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

const GoogleMapsWrapper: React.FC<Props> = ({ children, fallback }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: 'AIzaSyDmGs8jYs-PgU7PX8Fi5jmv1bPGdLXU0xA',
    libraries: ['places'], // always include all needed libraries
  });

  if (loadError) {
    console.error('Google Maps API failed to load:', loadError);
    return (
      <div>
        Failed to load Google Maps. Some features may not work properly.
      </div>
    );
  }

  // if (!isLoaded) return <div>Loading Google Maps...</div>;
  if (!isLoaded) {
    return <>{fallback ?? <DefaultMapFallback />}</>;
  }

  return <>{children}</>;
};

export default GoogleMapsWrapper;
